import React, { useState, useEffect, useMemo, useRef, startTransition, useDeferredValue } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { MagnifyingGlassIcon, BookmarkIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkIconSolid } from '@heroicons/react/24/solid';
import { usePageTitle } from '../hooks/usePageTitle';
import { businessService, BusinessListing } from '../services/businessService';
import { getFranchiseListings } from '../services/franchiseService';
import { savedListingsService } from '../services/savedListingsService';
import { useAuthContext } from '../contexts/AuthContext';
import { MAIN_CATEGORIES, SUB_CATEGORIES, BUYER_TYPES } from '../utils/categories';
import { CANADIAN_PROVINCES } from '../utils/locations';
import { getCachedListings, setCachedListings } from '../utils/cacheUtils';
import { Timestamp } from 'firebase/firestore';

// Helper function to safely convert Timestamp (or plain object from cache) to milliseconds
const timestampToMillis = (timestamp: Timestamp | { seconds?: number; nanoseconds?: number } | null | undefined): number => {
  if (!timestamp) return 0;
  
  // If it's a Firestore Timestamp object, use toMillis()
  if (timestamp instanceof Timestamp) {
    return timestamp.toMillis();
  }
  
  // If it's a plain object from cache (JSON.parse), convert manually
  if (typeof timestamp === 'object' && 'seconds' in timestamp && timestamp.seconds !== undefined) {
    return timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000;
  }
  
  return 0;
};

// Format digits-only string with commas for display (no decimals)
function formatPriceWithCommas(digits: string): string {
  const cleaned = digits.replace(/\D/g, '');
  return cleaned.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Initialize filter state from URL so back-navigation (e.g. listing → back) shows correct filters and count
function getInitialFiltersFromUrl(searchParams: URLSearchParams) {
  return {
    province: searchParams.get('province') || '',
    category: searchParams.get('category') || '',
    subcategory: searchParams.get('subcategory') || '',
    sort: searchParams.get('sort') || 'Newest First',
    view: searchParams.get('view') === '3' ? '3' as const : '1' as const,
    age: searchParams.get('age') || 'Anytime',
    buyerTypes: (searchParams.get('buyerTypes') || '').split(',').filter(Boolean) as string[],
    search: searchParams.get('search') || '',
    priceMin: (searchParams.get('priceMin') || '').replace(/\D/g, ''),
    priceMax: (searchParams.get('priceMax') || '').replace(/\D/g, ''),
  };
}

const BusinessListings: React.FC = () => {
  const { currentUser } = useAuthContext();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isFranchisesPage = location.pathname === '/franchises';
  const initialUrl = useRef(searchParams);
  const urlFilters = getInitialFiltersFromUrl(initialUrl.current);
  const [sortBy, setSortBy] = useState(urlFilters.sort);
  const [showCount, setShowCount] = useState('25');
  const [viewMode, setViewMode] = useState<'1' | '3'>(urlFilters.view);
  const [businesses, setBusinesses] = useState<BusinessListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false); // For progressive loading
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [savedBusinessIds, setSavedBusinessIds] = useState<Set<string>>(new Set());
  const [savingBusinessId, setSavingBusinessId] = useState<string | null>(null);
  const [featuredBusinesses, setFeaturedBusinesses] = useState<BusinessListing[]>([]);
  
  // Filter states – initialized from URL so back-navigation has correct filters (fixes "51 after back")
  const [selectedProvince, setSelectedProvince] = useState<string>(urlFilters.province);
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>(urlFilters.category);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>(urlFilters.subcategory);
  const [selectedBuyerTypes, setSelectedBuyerTypes] = useState<string[]>(urlFilters.buyerTypes);
  const [searchTerm, setSearchTerm] = useState(urlFilters.search);
  const [searchInput, setSearchInput] = useState(urlFilters.search); // Local state for search input
  const [selectedAgeOfListing, setSelectedAgeOfListing] = useState<string>(urlFilters.age);
  const [priceMinInput, setPriceMinInput] = useState<string>(urlFilters.priceMin);
  const [priceMaxInput, setPriceMaxInput] = useState<string>(urlFilters.priceMax);
  const [showBuyerTypesModal, setShowBuyerTypesModal] = useState(false);
  const [tempBuyerTypes, setTempBuyerTypes] = useState<string[]>([]); // Temporary state for modal
  const isInitialMount = useRef(true);
  const loadVersionRef = useRef(0); // Ignore stale fetch results so 51 cannot overwrite 2676

  // Load saved listings for current user
  useEffect(() => {
    const loadSavedListings = async () => {
      if (!currentUser) {
        setSavedBusinessIds(new Set());
        return;
      }

      try {
        const saved = await savedListingsService.getSavedListings(currentUser.uid);
        const savedIds = new Set(saved.map(sl => sl.businessId));
        setSavedBusinessIds(savedIds);
      } catch (error) {
        console.error('Error loading saved listings:', error);
      }
    };

    loadSavedListings();
  }, [currentUser]);

  // Load featured businesses based on category filter. On /franchises, only franchises (business.franchise=true).
  useEffect(() => {
    const loadFeaturedBusinesses = async () => {
      try {
        if (isFranchisesPage) {
          // On /franchises: featured = franchises only (from franchises collection)
          const franchiseFilters = {
            province: selectedProvince || undefined,
            category: selectedMainCategory || undefined,
            subcategory: selectedSubCategory || undefined,
          };
          const result = await getFranchiseListings(
            franchiseFilters,
            { field: 'createdAt', direction: 'desc' },
            20
          );
          const shuffled = [...result.businesses].sort(() => Math.random() - 0.5);
          setFeaturedBusinesses(shuffled.slice(0, 3));
        } else {
          // On /businesses: featured from getFeaturedBusinesses, then filter by category/province
          const allFeatured = await businessService.getFeaturedBusinesses(20);
          let filtered = allFeatured;
          if (selectedMainCategory) {
            filtered = allFeatured.filter(b => b.basic.category === selectedMainCategory);
          }
          if (selectedProvince) {
            filtered = filtered.filter(b => b.location.province === selectedProvince);
          }
          const shuffled = [...filtered].sort(() => Math.random() - 0.5);
          setFeaturedBusinesses(shuffled.slice(0, 3));
        }
      } catch (error) {
        console.error('Error loading featured businesses:', error);
        setFeaturedBusinesses([]);
      }
    };

    // Only load featured if no search term and no buyer type filters (to keep it simple)
    if (!searchTerm && selectedBuyerTypes.length === 0 && selectedAgeOfListing === 'Anytime') {
      loadFeaturedBusinesses();
    } else {
      setFeaturedBusinesses([]);
    }
  }, [isFranchisesPage, selectedMainCategory, selectedProvince, selectedSubCategory, searchTerm, selectedBuyerTypes, selectedAgeOfListing]);

  // Load businesses from Firestore (or franchises collection when on /franchises)
  useEffect(() => {
    const thisLoadVersion = ++loadVersionRef.current; // Ignore stale responses (e.g. first fetch with no category overwriting category fetch)
    const loadBusinesses = async () => {
      try {
        // Check if client-side filtering is needed (search, buyer types, age filter)
        const needsClientSideFiltering = !!searchTerm || selectedBuyerTypes.length > 0 || selectedAgeOfListing !== 'Anytime';
        // When category/province/subcategory is selected, fetch full set so count matches admin
        const hasCategoryOrLocationFilter = !!(selectedMainCategory || selectedSubCategory || selectedProvince);
        const pageSize = isFranchisesPage
          ? 1000
          : hasCategoryOrLocationFilter
            ? 3500
            : needsClientSideFiltering
              ? 1000
              : 100;

        if (isFranchisesPage) {
          // Read from dedicated franchises collection
          setError(null);
          setLoading(true);
          const priceMinNum = priceMinInput.trim() ? parseInt(priceMinInput.replace(/,/g, ''), 10) : undefined;
          const priceMaxNum = priceMaxInput.trim() ? parseInt(priceMaxInput.replace(/,/g, ''), 10) : undefined;
          const franchiseFilters = {
            province: selectedProvince || undefined,
            category: selectedMainCategory || undefined,
            subcategory: selectedSubCategory || undefined,
            ...(priceMinNum !== undefined && !Number.isNaN(priceMinNum) && priceMinNum > 0 && { priceMin: priceMinNum }),
            ...(priceMaxNum !== undefined && !Number.isNaN(priceMaxNum) && priceMaxNum > 0 && { priceMax: priceMaxNum }),
          };
          const franchiseSortField =
            sortBy === 'Price: Low to High' || sortBy === 'Price: High to Low'
              ? 'askingPrice'
              : 'createdAt';
          const franchiseSortDir =
            sortBy === 'Price: Low to High' || sortBy === 'Oldest First' ? 'asc' : 'desc';
          const result = await getFranchiseListings(
            franchiseFilters,
            { field: franchiseSortField, direction: franchiseSortDir },
            pageSize
          );
          if (loadVersionRef.current !== thisLoadVersion) return;
          setBusinesses(result.businesses);
          setTotalCount(result.total);
        } else {
          // Build filters for server-side filtering (businesses + ddfListings)
          const filters: any = {};
          if (selectedProvince) filters.province = selectedProvince;
          if (selectedMainCategory) filters.category = selectedMainCategory;
          if (selectedSubCategory) filters.subcategory = selectedSubCategory;
          const priceMinNum = priceMinInput.trim() ? parseInt(priceMinInput.replace(/,/g, ''), 10) : undefined;
          const priceMaxNum = priceMaxInput.trim() ? parseInt(priceMaxInput.replace(/,/g, ''), 10) : undefined;
          if (priceMinNum !== undefined && !Number.isNaN(priceMinNum) && priceMinNum > 0) filters.priceMin = priceMinNum;
          if (priceMaxNum !== undefined && !Number.isNaN(priceMaxNum) && priceMaxNum > 0) filters.priceMax = priceMaxNum;

          let sortField: 'basic.createdAt' | 'financial.askingPrice' = 'basic.createdAt';
          let sortDirection: 'asc' | 'desc' = 'desc';
          if (sortBy === 'Price: Low to High') {
            sortField = 'financial.askingPrice';
            sortDirection = 'asc';
          } else if (sortBy === 'Price: High to Low') {
            sortField = 'financial.askingPrice';
            sortDirection = 'desc';
          } else if (sortBy === 'Oldest First') {
            sortField = 'basic.createdAt';
            sortDirection = 'asc';
          }
          const sort = { field: sortField, direction: sortDirection };

          // Skip cache when filtering by category/province (payload too large for localStorage)
          const useCache = !hasCategoryOrLocationFilter;
          const cachedData = useCache
            ? getCachedListings<{ businesses: BusinessListing[]; totalCount: number }>(filters, sort, pageSize)
            : null;
          if (cachedData) {
            if (loadVersionRef.current !== thisLoadVersion) return;
            setBusinesses(cachedData.businesses);
            setTotalCount(cachedData.totalCount);
            setLoading(false);
            setIsRefreshing(true);
            return;
          } else {
            setLoading(true);
            // Clear stale count/list when fetching with category/province so we don't flash "out of 51" then "out of 2676"
            if (hasCategoryOrLocationFilter) {
              setBusinesses([]);
              setTotalCount(0);
            }
          }
          setError(null);

          const result = await businessService.getBusinesses(
            filters,
            sort,
            pageSize,
            undefined,
            { includeDDF: true, fetchMoreForClientFiltering: needsClientSideFiltering || hasCategoryOrLocationFilter }
          );
          if (loadVersionRef.current !== thisLoadVersion) return;
          // Use returned total when we have category/province filter or client-side filtering (matches admin count)
          let totalCountValue = result.businesses.length;
          if (hasCategoryOrLocationFilter || needsClientSideFiltering) {
            totalCountValue = result.total ?? result.businesses.length;
          } else {
            try {
              totalCountValue = await businessService.getTotalActiveBusinessCount();
            } catch (countError) {
              console.warn('Error getting total count, using fetched count:', countError);
            }
          }
          if (loadVersionRef.current !== thisLoadVersion) return;
          setBusinesses(result.businesses);
          setTotalCount(totalCountValue);
          if (useCache) {
            setCachedListings(filters, sort, pageSize, {
              businesses: result.businesses,
              totalCount: totalCountValue,
            });
          }
        }
      } catch (err) {
        if (loadVersionRef.current !== thisLoadVersion) return;
        setError('Failed to load businesses');
        console.error('Error loading businesses:', err);
      } finally {
        if (loadVersionRef.current === thisLoadVersion) {
          setLoading(false);
          setIsRefreshing(false);
        }
      }
    };

    loadBusinesses();
  }, [isFranchisesPage, selectedProvince, selectedMainCategory, selectedSubCategory, sortBy, searchTerm, selectedBuyerTypes, selectedAgeOfListing, priceMinInput, priceMaxInput]);

  // Defer heavy list so filter/sort work doesn't block the main thread (reduces scheduler violations)
  const deferredBusinesses = useDeferredValue(businesses);
  // Single filter pass: category, province, search, buyer types, price (no age) – used for display and age counts
  const baseFiltered = useMemo(() => {
    const priceMinNum = priceMinInput.trim() ? parseInt(priceMinInput.replace(/,/g, ''), 10) : undefined;
    const priceMaxNum = priceMaxInput.trim() ? parseInt(priceMaxInput.replace(/,/g, ''), 10) : undefined;
    return deferredBusinesses.filter(business => {
      if (selectedProvince && business.location.province !== selectedProvince) return false;
      if (selectedMainCategory && business.basic.category !== selectedMainCategory) return false;
      if (selectedSubCategory && business.basic.subcategory !== selectedSubCategory) return false;
      if (selectedBuyerTypes.length > 0) {
        const businessBuyerTypes = business.basic.buyerTypes || [];
        if (!selectedBuyerTypes.some(bt => businessBuyerTypes.includes(bt))) return false;
      }
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        if (!(
          business.basic.title.toLowerCase().includes(searchLower) ||
          business.basic.description.toLowerCase().includes(searchLower) ||
          business.location.city.toLowerCase().includes(searchLower) ||
          business.location.province.toLowerCase().includes(searchLower) ||
          business.basic.category.toLowerCase().includes(searchLower) ||
          business.basic.subcategory.toLowerCase().includes(searchLower)
        )) return false;
      }
      if (priceMinNum !== undefined && !Number.isNaN(priceMinNum) && business.financial.askingPrice < priceMinNum) return false;
      if (priceMaxNum !== undefined && !Number.isNaN(priceMaxNum) && business.financial.askingPrice > priceMaxNum) return false;
      return true;
    });
  }, [deferredBusinesses, selectedProvince, selectedMainCategory, selectedSubCategory, selectedBuyerTypes, searchTerm, priceMinInput, priceMaxInput]);

  // Apply age filter on top of baseFiltered (one extra pass, but on a usually smaller list)
  const filteredBusinesses = useMemo(() => {
    if (!selectedAgeOfListing || selectedAgeOfListing === 'Anytime') return baseFiltered;
    const now = Date.now();
    return baseFiltered.filter(business => {
      const createdAt = timestampToMillis(business.basic.createdAt);
      const ageInMs = now - createdAt;
      const ageInDays = ageInMs / (1000 * 60 * 60 * 24);
      switch (selectedAgeOfListing) {
        case 'Last 3 Days': return ageInDays <= 3;
        case 'Last 14 Days': return ageInDays <= 14;
        case 'Last Month': return ageInDays <= 30;
        case 'Last 3 Months': return ageInDays <= 90;
        default: return true;
      }
    });
  }, [baseFiltered, selectedAgeOfListing]);

  // Reset to page 1 when filters, sortBy, or showCount changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProvince, selectedMainCategory, selectedSubCategory, selectedBuyerTypes, searchTerm, selectedAgeOfListing, showCount, sortBy, priceMinInput, priceMaxInput]);

  // Update URL with all filters (but not on initial mount – state was initialized from URL)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const params = new URLSearchParams();
    if (selectedProvince) {
      params.append('province', selectedProvince);
    }
    if (selectedMainCategory) {
      params.append('category', selectedMainCategory);
    }
    if (selectedSubCategory) {
      params.append('subcategory', selectedSubCategory);
    }
    if (priceMinInput.trim()) {
      params.append('priceMin', priceMinInput.trim());
    }
    if (priceMaxInput.trim()) {
      params.append('priceMax', priceMaxInput.trim());
    }
    if (sortBy && sortBy !== 'Newest First') {
      params.append('sort', sortBy);
    }
    if (viewMode === '3') {
      params.append('view', '3');
    }
    if (selectedAgeOfListing && selectedAgeOfListing !== 'Anytime') {
      params.append('age', selectedAgeOfListing);
    }
    if (selectedBuyerTypes.length > 0) {
      params.append('buyerTypes', selectedBuyerTypes.join(','));
    }
    if (searchTerm) {
      params.append('search', searchTerm);
    }
    
    // Update URL (replace to avoid adding to history)
    const newParamsString = params.toString();
    const currentParamsString = searchParams.toString();
    // Only update if params actually changed
    if (newParamsString !== currentParamsString) {
      setSearchParams(params, { replace: true });
    }
  }, [selectedProvince, selectedMainCategory, selectedSubCategory, sortBy, viewMode, selectedAgeOfListing, selectedBuyerTypes, searchTerm, priceMinInput, priceMaxInput, searchParams, setSearchParams]);

  // Transform and sort Firestore data to match the expected format
  const transformedBusinesses = useMemo(() => {
    const transformed = filteredBusinesses.map(business => ({
      id: business.id || '',
      title: business.basic.title,
      location: `${business.location.city}, ${business.location.province}`,
      description: business.basic.description,
      askingPrice: `$${business.financial.askingPrice.toLocaleString()} (${business.financial.currency})`,
      revenue: `$${(business.financial.annualRevenue / 1000).toFixed(0)}K - $${(business.financial.annualRevenue * 1.5 / 1000).toFixed(0)}K (${business.financial.currency})`,
      cashFlow: `$${(business.financial.annualCashFlow / 1000).toFixed(0)}K - $${(business.financial.annualCashFlow * 1.2 / 1000).toFixed(0)}K (${business.financial.currency})`,
      tags: business.basic.tags,
      isNew: business.basic.createdAt && 
             (Date.now() - timestampToMillis(business.basic.createdAt)) < (7 * 24 * 60 * 60 * 1000), // 7 days
      category: business.basic.category,
      subcategory: business.basic.subcategory,
      seoUrl: business.basic.seoUrl || business.id || '',
      imageUrl: business.media.images && business.media.images.length > 0 && business.media.images[0] 
        ? business.media.images[0] 
        : null,
      // Keep original data for sorting
      _originalBusiness: business
    }));

    // Apply sorting (default is Newest First)
    if (sortBy === 'Price: Low to High') {
      transformed.sort((a, b) => a._originalBusiness.financial.askingPrice - b._originalBusiness.financial.askingPrice);
    } else if (sortBy === 'Price: High to Low') {
      transformed.sort((a, b) => b._originalBusiness.financial.askingPrice - a._originalBusiness.financial.askingPrice);
    } else if (sortBy === 'Newest First' || !sortBy) {
      transformed.sort((a, b) => {
        const aTime = timestampToMillis(a._originalBusiness.basic.createdAt);
        const bTime = timestampToMillis(b._originalBusiness.basic.createdAt);
        return bTime - aTime;
      });
    } else if (sortBy === 'Oldest First') {
      transformed.sort((a, b) => {
        const aTime = timestampToMillis(a._originalBusiness.basic.createdAt);
        const bTime = timestampToMillis(b._originalBusiness.basic.createdAt);
        return aTime - bTime;
      });
    }

    return transformed;
  }, [filteredBusinesses, sortBy]);

  // Pagination calculations - always use client-side pagination on fetched batch
  const itemsPerPage = parseInt(showCount, 10) || 25;
  
  // Determine if filters are active
  const hasPriceFilter = !!(priceMinInput.trim() || priceMaxInput.trim());
  const hasActiveFilters = isFranchisesPage || selectedProvince || selectedMainCategory || selectedSubCategory || 
                          selectedBuyerTypes.length > 0 || searchTerm || selectedAgeOfListing !== 'Anytime' || hasPriceFilter;
  
  // Use filtered count when filters are active, otherwise use total count
  const displayCount = hasActiveFilters ? transformedBusinesses.length : totalCount;
  
  // Calculate total pages based on display count (filtered or total)
  const totalPages = Math.max(1, Math.ceil(displayCount / itemsPerPage));
  
  // Ensure currentPage doesn't exceed total pages
  const effectiveCurrentPage = Math.min(currentPage, totalPages);
  const effectiveStartIndex = (effectiveCurrentPage - 1) * itemsPerPage;
  const effectiveEndIndex = Math.min(effectiveStartIndex + itemsPerPage, transformedBusinesses.length);
  const paginatedBusinesses = transformedBusinesses.slice(effectiveStartIndex, effectiveEndIndex);
  
  // Ensure currentPage doesn't exceed total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Handle save/unsave listing
  const handleSaveListing = async (businessId: string) => {
    if (!currentUser) {
      return;
    }

    try {
      setSavingBusinessId(businessId);
      const isSaved = savedBusinessIds.has(businessId);
      
      if (isSaved) {
        // Remove from saved listings
        await savedListingsService.removeSavedListing(currentUser.uid, businessId);
        setSavedBusinessIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(businessId);
          return newSet;
        });
      } else {
        // Add to saved listings
        await savedListingsService.saveListing(currentUser.uid, businessId);
        setSavedBusinessIds(prev => new Set(prev).add(businessId));
      }
    } catch (error) {
      console.error('Error saving/unsaving listing:', error);
      alert('Failed to save listing. Please try again.');
    } finally {
      setSavingBusinessId(null);
    }
  };

  // Scroll to top helper
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 7;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage <= 4) {
        // Near the beginning
        for (let i = 2; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Near the end
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Age-of-listing counts from baseFiltered; cap iterations when list is huge to avoid main-thread violations
  const AGE_COUNT_MAX_ITER = 600;
  const ageOfListingCounts = useMemo(() => {
    if (baseFiltered.length === 0) {
      return { 'Anytime': 0, 'Last 3 Days': 0, 'Last 14 Days': 0, 'Last Month': 0, 'Last 3 Months': 0 };
    }
    const now = Date.now();
    const threeDaysMs = 3 * 24 * 60 * 60 * 1000;
    const fourteenDaysMs = 14 * 24 * 60 * 60 * 1000;
    const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
    const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
    const counts = { 'Anytime': baseFiltered.length, 'Last 3 Days': 0, 'Last 14 Days': 0, 'Last Month': 0, 'Last 3 Months': 0 };
    const step = baseFiltered.length > AGE_COUNT_MAX_ITER ? Math.ceil(baseFiltered.length / AGE_COUNT_MAX_ITER) : 1;
    for (let i = 0; i < baseFiltered.length; i += step) {
      const business = baseFiltered[i];
      const createdAt = timestampToMillis(business.basic.createdAt);
      if (createdAt === 0) continue;
      const ageInMs = now - createdAt;
      if (ageInMs <= threeDaysMs) {
        counts['Last 3 Days'] += step;
        counts['Last 14 Days'] += step;
        counts['Last Month'] += step;
        counts['Last 3 Months'] += step;
      } else if (ageInMs <= fourteenDaysMs) {
        counts['Last 14 Days'] += step;
        counts['Last Month'] += step;
        counts['Last 3 Months'] += step;
      } else if (ageInMs <= thirtyDaysMs) {
        counts['Last Month'] += step;
        counts['Last 3 Months'] += step;
      } else if (ageInMs <= ninetyDaysMs) {
        counts['Last 3 Months'] += step;
      }
    }
    return counts;
  }, [baseFiltered]);

  const filterOptions = {
    propertyFilters: [
      { name: 'Real Property', count: 376 },
      { name: 'Lease', count: 1027 },
      { name: 'Relocatable', count: 362 }
    ],
    businessTypes: [
      { name: 'Work From Home', count: 494 },
      { name: 'Accommodation Included', count: 72 },
      { name: 'Franchise', count: 862 },
      { name: 'Business Opportunity', count: 101 },
      { name: 'Price Reduced', count: 72 },
      { name: 'Mid Market', count: 580 },
      { name: 'Distressed', count: 23 },
      { name: 'Low Cost', count: 260 },
      { name: 'Owner Financed', count: 172 },
      { name: 'Hidden Gem', count: 15 },
      { name: 'Quick Sale', count: 292 }
    ],
    managementTypes: [
      { name: 'Owner Managed', count: 302 },
      { name: 'Employee Owned (ESOP)', count: 2 },
      { name: 'Absentee Ownership', count: 38 }
    ],
    ageOfListing: [
      { 
        name: 'Anytime', 
        // Use totalCount when no filters are applied, otherwise use the filtered count
        count: (selectedProvince || selectedMainCategory || selectedSubCategory || selectedBuyerTypes.length > 0 || searchTerm)
          ? ageOfListingCounts['Anytime']
          : totalCount
      },
      { name: 'Last 3 Days', count: ageOfListingCounts['Last 3 Days'] },
      { name: 'Last 14 Days', count: ageOfListingCounts['Last 14 Days'] },
      { name: 'Last Month', count: ageOfListingCounts['Last Month'] },
      { name: 'Last 3 Months', count: ageOfListingCounts['Last 3 Months'] }
    ]
  };



  // SEO-optimized content
  const pageLabel = isFranchisesPage ? 'Franchises for Sale' : 'Businesses for Sale';
  const seoTitle = isFranchisesPage
    ? `Franchises for Sale - Browse ${filteredBusinesses.length > 0 ? filteredBusinesses.length : totalCount}+ Franchise Listings | BizBuyMax.com`
    : `Businesses for Sale - Browse ${filteredBusinesses.length > 0 ? filteredBusinesses.length : totalCount}+ Business Listings | BizBuyMax.com`;
  const seoDescription = isFranchisesPage
    ? "Browse franchises for sale across North America. Find franchise resales and opportunities. Filter by location, price, and category."
    : "Browse thousands of businesses for sale across North America. Find restaurants, retail, services, franchises and more. Filter by location, price, and business type. Start your business journey today.";
  const seoKeywords = isFranchisesPage
    ? "franchises for sale, franchise listings, buy franchise, franchise resale, Canada, Alberta, Ontario, BC"
    : "businesses for sale, business listings, buy business, Canada, Alberta, Ontario, BC, restaurants, retail, services, franchises";

  // Update page title on navigation
  usePageTitle(seoTitle);

  // Structured data for search engines
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": pageLabel,
    "description": isFranchisesPage ? "Browse franchises for sale across North America" : "Browse thousands of businesses for sale across North America",
    "numberOfItems": filteredBusinesses.length,
    "itemListElement": transformedBusinesses.map((business, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "LocalBusiness",
        "name": business.title,
        "description": business.description,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": business.location.split(',')[0],
          "addressRegion": business.location.split(',')[1]?.trim(),
          "addressCountry": "CA"
        },
        "priceRange": business.askingPrice,
        "category": business.category
      }
    }))
  };

  // Skeleton loading component
  const BusinessSkeleton = ({ viewMode }: { viewMode: '1' | '3' }) => {
    if (viewMode === '3') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-pulse">
              <div className="h-[200px] bg-gray-200"></div>
              <div className="p-4">
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-5 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="space-y-6">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-pulse">
            <div className="flex flex-col md:flex-row">
              <div className="w-full md:w-[200px] h-[200px] bg-gray-200"></div>
              <div className="p-6 flex-1">
                <div className="h-6 bg-gray-200 rounded mb-3 w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded mb-2 w-1/2"></div>
                <div className="flex gap-2 mb-4">
                  <div className="h-6 bg-gray-200 rounded w-20"></div>
                  <div className="h-6 bg-gray-200 rounded w-20"></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="text-red-600 text-lg mb-4">⚠️ {error}</div>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoTitle}</title>
        <meta name="title" content={seoTitle} />
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://BizBuyMax.com/businesses" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content="https://BizBuyMax.com/businesses-og.jpg" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://BizBuyMax.com/businesses" />
        <meta property="twitter:title" content={seoTitle} />
        <meta property="twitter:description" content={seoDescription} />
        <meta property="twitter:image" content="https://BizBuyMax.com/businesses-og.jpg" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="BizBuyMax.com" />
        <meta name="geo.region" content="CA" />
        <meta name="geo.placename" content="Canada" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://BizBuyMax.com/businesses" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-4 lg:mb-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-gray-900">
                    {isFranchisesPage ? 'Franchises for Sale in North America' : 'Businesses for Sale in North America'}
                  </h1>
                  {isRefreshing && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" title="Refreshing..."></div>
                  )}
                </div>
                <p className="text-gray-600 mt-1">
                  {loading && businesses.length === 0 ? (
                    'Loading businesses...'
                  ) : (
                    <>
                      {paginatedBusinesses.length > 0 
                        ? `Showing ${effectiveStartIndex + 1}-${effectiveEndIndex} of ${displayCount.toLocaleString()} businesses`
                        : `${displayCount.toLocaleString()} businesses available`
                      }
                      {hasActiveFilters && ' • Filtered'}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <div className="lg:w-1/4">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                  {hasActiveFilters && (
                    <button
                      onClick={() => {
                        startTransition(() => {
                          setSelectedProvince('');
                          setSelectedMainCategory('');
                          setSelectedSubCategory('');
                          setSelectedBuyerTypes([]);
                          setSearchTerm('');
                          setSearchInput('');
                          setSelectedAgeOfListing('Anytime');
                          setPriceMinInput('');
                          setPriceMaxInput('');
                          setCurrentPage(1);
                        });
                      }}
                      className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                
                {/* Search Keywords - Moved to top */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Search Keywords</h3>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search keywords..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          startTransition(() => {
                            setSearchTerm(searchInput);
                            setCurrentPage(1);
                          });
                        }
                      }}
                      className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm mb-2"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        startTransition(() => {
                          setSearchTerm(searchInput);
                          setCurrentPage(1);
                        });
                      }}
                      className="flex-1 px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium transition-colors text-sm"
                    >
                      Search
                    </button>
                    {searchTerm && (
                      <button
                        onClick={() => {
                          startTransition(() => {
                            setSearchTerm('');
                            setSearchInput('');
                            setCurrentPage(1);
                          });
                        }}
                        className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium transition-colors text-sm whitespace-nowrap"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Province Filter */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Province</h3>
                  <select
                    value={selectedProvince}
                    onChange={(e) => startTransition(() => setSelectedProvince(e.target.value))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Provinces</option>
                    {CANADIAN_PROVINCES.map(province => (
                      <option key={province} value={province}>{province}</option>
                    ))}
                  </select>
                </div>

                {/* Min/Max Asking Price */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Asking Price</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                      <label className="sr-only">Min price</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="Min"
                        value={formatPriceWithCommas(priceMinInput)}
                        onChange={(e) => startTransition(() => setPriceMinInput(e.target.value.replace(/\D/g, '')))}
                        className="w-full border border-gray-300 rounded-md pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                      <label className="sr-only">Max price</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="Max"
                        value={formatPriceWithCommas(priceMaxInput)}
                        onChange={(e) => startTransition(() => setPriceMaxInput(e.target.value.replace(/\D/g, '')))}
                        className="w-full border border-gray-300 rounded-md pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">e.g. $100,000 or $1,500,000 (no decimals)</p>
                </div>

                {/* Category Filter */}
                <div className={selectedMainCategory ? "mb-1" : "mb-6"}>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Category</h3>
                  <select
                    value={selectedMainCategory}
                    onChange={(e) => {
                      startTransition(() => {
                        setSelectedMainCategory(e.target.value);
                        setSelectedSubCategory(''); // Reset subcategory when main category changes
                      });
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Categories</option>
                    {MAIN_CATEGORIES.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                {/* Subcategory Filter - Moved right below category with arrow */}
                {selectedMainCategory && (
                  <div className="mb-6 ml-4">
                    <div className="relative">
                      <select
                        value={selectedSubCategory}
                        onChange={(e) => startTransition(() => setSelectedSubCategory(e.target.value))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 appearance-none"
                      >
                        <option value="">All Subcategories</option>
                        {SUB_CATEGORIES[selectedMainCategory]?.map(subcategory => (
                          <option key={subcategory} value={subcategory}>{subcategory}</option>
                        ))}
                      </select>
                      <ChevronRightIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 rotate-45 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                )}

                {/* Buyer Types Filter - Popup Modal */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Buyer Types</h3>
                  <button
                    onClick={() => {
                      setTempBuyerTypes([...selectedBuyerTypes]);
                      setShowBuyerTypesModal(true);
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {selectedBuyerTypes.length > 0 
                      ? `${selectedBuyerTypes.length} selected`
                      : 'Select buyer types...'
                    }
                  </button>
                  
                  {/* Selected buyer types display */}
                  {selectedBuyerTypes.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedBuyerTypes.map((buyerType) => (
                        <span
                          key={buyerType}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-md"
                        >
                          {buyerType}
                          <button
                            onClick={() => {
                              startTransition(() => setSelectedBuyerTypes(prev => prev.filter(bt => bt !== buyerType)));
                            }}
                            className="hover:text-primary-900"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                      <button
                        onClick={() => startTransition(() => setSelectedBuyerTypes([]))}
                        className="text-xs text-primary-600 hover:text-primary-800 px-2 py-1"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>

                {/* Buyer Types Modal */}
                {showBuyerTypesModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
                      <div className="flex items-center justify-between p-4 border-b">
                        <h3 className="text-lg font-semibold text-gray-900">Select Buyer Types</h3>
                        <button
                          onClick={() => setShowBuyerTypesModal(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="p-4 overflow-y-auto flex-1">
                        <div className="space-y-2">
                          {BUYER_TYPES.map((buyerType) => (
                            <label key={buyerType} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                              <input
                                type="checkbox"
                                checked={tempBuyerTypes.includes(buyerType)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setTempBuyerTypes(prev => [...prev, buyerType]);
                                  } else {
                                    setTempBuyerTypes(prev => prev.filter(bt => bt !== buyerType));
                                  }
                                }}
                                className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                              />
                              <span className="ml-2 text-sm text-gray-700">{buyerType}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-2 p-4 border-t">
                        <button
                          onClick={() => {
                            setTempBuyerTypes([]);
                          }}
                          className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                        >
                          Clear All
                        </button>
                        <button
                          onClick={() => {
                            startTransition(() => setSelectedBuyerTypes([...tempBuyerTypes]));
                            setShowBuyerTypesModal(false);
                          }}
                          className="px-4 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Age of Listing */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Age of Listing</h3>
                  <div className="space-y-2">
                    {filterOptions.ageOfListing.map((filter) => (
                      <label key={filter.name} className="flex items-center">
                        <input
                          type="radio"
                          name="ageOfListing"
                          value={filter.name}
                          checked={selectedAgeOfListing === filter.name}
                          onChange={(e) => startTransition(() => setSelectedAgeOfListing(e.target.value))}
                          className="rounded-full border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">{filter.name}</span>
                        <span className="ml-auto text-xs text-gray-500">({filter.count})</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:w-3/4">
              {/* Sort and View Options */}
              <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center space-x-4 mb-4 sm:mb-0">
                    <label className="text-sm font-medium text-gray-700">Sort by:</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="Newest First">Newest First</option>
                      <option value="Price: Low to High">Price: Low to High</option>
                      <option value="Price: High to Low">Price: High to Low</option>
                      <option value="Oldest First">Oldest First</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    {/* View Mode Toggle */}
                    <label className="text-sm font-medium text-gray-700">View:</label>
                    <div className="flex border border-gray-300 rounded-md overflow-hidden">
                      <button
                        onClick={() => setViewMode('1')}
                        className={`px-3 py-1 text-sm transition-colors ${
                          viewMode === '1'
                            ? 'bg-primary-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                        title="1 per row"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setViewMode('3')}
                        className={`px-3 py-1 text-sm transition-colors border-l border-gray-300 ${
                          viewMode === '3'
                            ? 'bg-primary-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                        title="3 per row"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                      </button>
                    </div>
                    
                    <label className="text-sm font-medium text-gray-700">Show:</label>
                    <select
                      value={showCount}
                      onChange={(e) => {
                        startTransition(() => {
                          setShowCount(e.target.value);
                          setCurrentPage(1); // Reset to first page when changing items per page
                        });
                      }}
                      className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Featured Businesses Section */}
              {featuredBusinesses.length > 0 && (
                <div className="mb-8">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">
                          {isFranchisesPage ? 'Featured Franchises' : 'Featured'}
                        </h2>
                        <p className="text-gray-600 text-sm">
                          {isFranchisesPage
                            ? (selectedMainCategory
                                ? `Hand-picked featured franchises in ${selectedMainCategory}`
                                : 'Discover our hand-picked featured franchises')
                            : (selectedMainCategory
                                ? `Hand-picked featured businesses in ${selectedMainCategory}`
                                : 'Discover our hand-picked featured businesses with exceptional potential')
                          }
                        </p>
                      </div>
                      <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full">
                        FEATURED
                      </span>
                    </div>
                  
                    <div className="space-y-8">
                      {featuredBusinesses.map((business) => {
                        const isSaved = savedBusinessIds.has(business.id || '');
                        const imageUrl = business.media.images && business.media.images.length > 0 
                          ? business.media.images[0] 
                          : null;
                        
                        return (
                          <div key={business.id} className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow overflow-hidden">
                          <div className="flex flex-col md:flex-row">
                            {/* Image Section */}
                            {imageUrl && (
                              <Link to={business.basic.seoUrl ? `/business/${business.basic.seoUrl}` : `/business/${business.id}`} className="flex-shrink-0 group">
                                <div className="w-full md:w-[250px] h-[250px] relative overflow-hidden bg-gray-100">
                                <img
                                  src={imageUrl}
                                  alt={business.basic.title}
                                  className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                                  style={{ imageRendering: '-webkit-optimize-contrast' }}
                                  loading="lazy"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.parentElement!.style.display = 'none';
                                  }}
                                />
                                <div className="absolute top-2 left-2 flex gap-2">
                                  <span className="bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg">
                                    FEATURED
                                  </span>
                                  {business.basic.verified && (
                                    <span className="bg-blue-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg">
                                      VERIFIED
                                    </span>
                                  )}
                                </div>
                              </div>
                            </Link>
                            )}
                            
                            {/* Content Section */}
                            <div className="p-6 flex-1">
                              {/* Badges if no image */}
                              {!imageUrl && (
                                <div className="flex items-center justify-between mb-3">
                                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                    FEATURED
                                  </span>
                                  {business.basic.verified && (
                                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                      VERIFIED
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <h2 className="text-xl font-semibold text-gray-900">
                                    <Link to={business.basic.seoUrl ? `/business/${business.basic.seoUrl}` : `/business/${business.id}`} className="hover:text-primary-600">
                                      {business.basic.title}
                                    </Link>
                                  </h2>
                                </div>
                                
                                <div className="flex items-center gap-3 mb-2 text-sm">
                                  <div className="flex items-center text-gray-600">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {business.location.city}, {business.location.province}
                                  </div>
                                  <span className="text-gray-400">•</span>
                                  <div className="text-gray-600">{business.basic.category}</div>
                                </div>
                                
                                <p className="text-gray-600 mb-3 line-clamp-2">{business.basic.shortDescription}</p>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-500">Asking Price:</span>
                                    <div className="font-semibold text-gray-900">${business.financial.askingPrice.toLocaleString()}</div>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Revenue:</span>
                                    <div className="font-semibold text-gray-900">${(business.financial.annualRevenue / 1000).toFixed(0)}K - ${(business.financial.annualRevenue * 1.5 / 1000).toFixed(0)}K</div>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Cash Flow:</span>
                                    <div className="font-semibold text-gray-900">${(business.financial.annualCashFlow / 1000).toFixed(0)}K - ${(business.financial.annualCashFlow * 1.2 / 1000).toFixed(0)}K</div>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="ml-4 flex flex-col items-end space-y-2">
                                {currentUser ? (
                                  <button
                                    onClick={() => handleSaveListing(business.id!)}
                                    disabled={savingBusinessId === business.id}
                                    className={`transition-colors ${
                                      isSaved
                                        ? 'text-primary-600 hover:text-primary-700'
                                        : 'text-gray-400 hover:text-gray-600'
                                    } ${savingBusinessId === business.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    title={isSaved ? 'Remove from saved' : 'Save listing'}
                                  >
                                    {savingBusinessId === business.id ? (
                                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                                    ) : isSaved ? (
                                      <BookmarkIconSolid className="h-5 w-5" />
                                    ) : (
                                      <BookmarkIcon className="h-5 w-5" />
                                    )}
                                  </button>
                                ) : (
                                  <Link
                                    to="/signin"
                                    className="text-gray-400 hover:text-gray-600"
                                    title="Sign in to save listings"
                                  >
                                    <BookmarkIcon className="h-5 w-5" />
                                  </Link>
                                )}
                              </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Business Listings */}
              {loading && businesses.length === 0 ? (
                <BusinessSkeleton viewMode={viewMode} />
              ) : viewMode === '3' ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {paginatedBusinesses.length === 0 ? (
                    <div className="col-span-3 text-center py-12">
                      <div className="text-gray-500 text-lg mb-4">No businesses found</div>
                      <p className="text-gray-400">Try adjusting your filters or search terms</p>
                    </div>
                  ) : (
                    paginatedBusinesses.map((business) => (
                      <div key={business.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
                        {/* Image Section */}
                        {business.imageUrl && (
                          <Link to={business.seoUrl ? `/business/${business.seoUrl}` : `/business/${business.id}`} className="block group">
                            <div className="h-[200px] relative overflow-hidden bg-gray-100">
                              <img
                                src={business.imageUrl}
                                alt={business.title}
                                className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                                style={{ imageRendering: '-webkit-optimize-contrast' }}
                                loading="lazy"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.parentElement!.style.display = 'none';
                                }}
                              />
                              {business.isNew && (
                                <span className="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg">
                                  NEW
                                </span>
                              )}
                            </div>
                          </Link>
                        )}
                        
                        <div className="p-4">
                          {business.isNew && !business.imageUrl && (
                            <div className="flex items-center justify-between mb-3">
                              <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                NEW
                              </span>
                            </div>
                          )}
                          
                          <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                            <Link to={business.seoUrl ? `/business/${business.seoUrl}` : `/business/${business.id}`} className="hover:text-primary-600">
                              {business.title}
                            </Link>
                          </h3>
                          
                          <div className="mb-2 text-sm">
                            <div className="flex items-center text-gray-600 mb-1">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              {business.location}
                            </div>
                            <div className="text-gray-600">
                              {business.category}
                            </div>
                          </div>
                          
                          <div className="text-lg font-bold text-primary-600">
                            {business.askingPrice}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {paginatedBusinesses.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-gray-500 text-lg mb-4">No businesses found</div>
                      <p className="text-gray-400">Try adjusting your filters or search terms</p>
                    </div>
                  ) : (
                    paginatedBusinesses.map((business) => (
                      <div key={business.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
                        <div className="flex flex-col md:flex-row">
                        {/* Image Section */}
                        {business.imageUrl && (
                          <Link to={business.seoUrl ? `/business/${business.seoUrl}` : `/business/${business.id}`} className="flex-shrink-0 group">
                            <div className="w-full md:w-[200px] h-[200px] relative overflow-hidden bg-gray-100">
                              <img
                                src={business.imageUrl}
                                alt={business.title}
                                className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                                style={{ imageRendering: '-webkit-optimize-contrast' }}
                                loading="lazy"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.parentElement!.style.display = 'none';
                                }}
                              />
                              {business.isNew && (
                                <span className="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg">
                                  NEW
                                </span>
                              )}
                            </div>
                          </Link>
                        )}
                        
                          {/* Content Section */}
                          <div className="p-6 flex-1">
                            <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <h2 className="text-xl font-semibold text-gray-900">
                                  <Link to={business.seoUrl ? `/business/${business.seoUrl}` : `/business/${business.id}`} className="hover:text-primary-600">
                                    {business.title}
                                  </Link>
                                </h2>
                                {business.isNew && !business.imageUrl && (
                                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                    NEW
                                  </span>
                                )}
                              </div>
                              
                              <div className="flex items-center gap-3 mb-2 text-sm">
                                <div className="flex items-center text-gray-600">
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  {business.location}
                                </div>
                              </div>
                              
                              <div className="flex flex-wrap gap-2 mb-4">
                                {business.tags.slice(0, 4).map((tag, index) => (
                                  <span
                                    key={index}
                                    className="bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-0.5 rounded"
                                  >
                                    {tag}
                                  </span>
                                ))}
                                {business.tags.length > 4 && (
                                  <span className="text-gray-500 text-xs font-medium px-2.5 py-0.5">
                                    +{business.tags.length - 4} more
                                  </span>
                                )}
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">Asking Price:</span>
                                  <div className="font-semibold text-gray-900">{business.askingPrice}</div>
                                </div>
                                <div>
                                  <span className="text-gray-500">Revenue:</span>
                                  <div className="font-semibold text-gray-900">{business.revenue}</div>
                                </div>
                                <div>
                                  <span className="text-gray-500">Cash Flow:</span>
                                  <div className="font-semibold text-gray-900">{business.cashFlow}</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="ml-4 flex flex-col items-end space-y-2">
                              {currentUser ? (
                                <button
                                  onClick={() => handleSaveListing(business._originalBusiness.id!)}
                                  disabled={savingBusinessId === business._originalBusiness.id}
                                  className={`transition-colors ${
                                    savedBusinessIds.has(business._originalBusiness.id || '')
                                      ? 'text-primary-600 hover:text-primary-700'
                                      : 'text-gray-400 hover:text-gray-600'
                                  } ${savingBusinessId === business._originalBusiness.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  title={savedBusinessIds.has(business._originalBusiness.id || '') ? 'Remove from saved' : 'Save listing'}
                                >
                                  {savingBusinessId === business._originalBusiness.id ? (
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                                  ) : savedBusinessIds.has(business._originalBusiness.id || '') ? (
                                    <BookmarkIconSolid className="h-5 w-5" />
                                  ) : (
                                    <BookmarkIcon className="h-5 w-5" />
                                  )}
                                </button>
                              ) : (
                                <Link
                                  to="/signin"
                                  className="text-gray-400 hover:text-gray-600"
                                  title="Sign in to save listings"
                                >
                                  <BookmarkIcon className="h-5 w-5" />
                                </Link>
                              )}
                            </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Pagination */}
              {paginatedBusinesses.length > 0 && (
                <div className="mt-8 flex flex-col items-center space-y-4">
                  <div className="text-sm text-gray-600">
                    Showing {effectiveStartIndex + 1} to {effectiveEndIndex} of {displayCount.toLocaleString()} businesses
                    {hasActiveFilters && ' • Filtered'}
                  </div>
                  {totalPages > 1 && (
                    <nav className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setCurrentPage(prev => Math.max(1, prev - 1));
                          scrollToTop();
                        }}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      {getPageNumbers().map((page, index) => {
                        if (page === '...') {
                          return (
                            <span key={`ellipsis-${index}`} className="px-3 py-2 text-sm text-gray-500">
                              ...
                            </span>
                          );
                        }
                        return (
                          <button
                            key={page}
                            onClick={() => {
                              setCurrentPage(page as number);
                              scrollToTop();
                            }}
                            className={`px-3 py-2 text-sm font-medium rounded-md border ${
                              currentPage === page
                                ? 'text-white bg-primary-600 border-primary-600'
                                : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => {
                          setCurrentPage(prev => Math.min(totalPages, prev + 1));
                          scrollToTop();
                        }}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BusinessListings; 